import React, { useMemo } from 'react';
import { Card, Empty, Select, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import ErrorFallback from '@components/common/ErrorFallback';
import {
  VisaApplicationReportItem,
  DashboardDateRangeEnum,
} from '../types/visa-application-report.types';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export interface WorkflowOption {
  label: string;
  value: string;
}

export interface VisaApplicationReportTableProps {
  data?: VisaApplicationReportItem[];
  loading?: boolean;
  error?: unknown;
  dateRange: DashboardDateRangeEnum;
  onDateRangeChange: (value: DashboardDateRangeEnum) => void;
  workflowOptions: WorkflowOption[];
  selectedWorkflowIds: string[];
  onWorkflowChange: (values: string[]) => void;
}

interface MetricRow {
  key: string;
  metric: string;
  [date: string]: number | string | undefined;
}

const VisaApplicationReportTable: React.FC<VisaApplicationReportTableProps> = ({
  data = [],
  loading = false,
  error,
  dateRange,
  onDateRangeChange,
  workflowOptions,
  selectedWorkflowIds,
  onWorkflowChange,
}) => {
  const { t } = useTranslation();

  const DATE_RANGE_LABELS: Record<DashboardDateRangeEnum, string> = useMemo(
    () => ({
      [DashboardDateRangeEnum.DAY]: t('applicationReport.dateRange.day'),
      [DashboardDateRangeEnum.MONTH]: t('applicationReport.dateRange.month'),
      [DashboardDateRangeEnum.QUARTER]: t('applicationReport.dateRange.quarter'),
    }),
    [t]
  );

  const groupedByWorkflow = useMemo(() => {
    const groups: Record<string, VisaApplicationReportItem[]> = {};

    data.forEach((item) => {
      const key = item.workflowId || item.workflowNames?.[0] || 'All Workflows';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    return groups;
  }, [data]);

  const formatDateToYYYYMMDD = (dateString: string): string => {
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  };

  const buildTableForWorkflow = (items: VisaApplicationReportItem[]) => {
    const dates = Array.from(new Set(items.map((i) => i.date))).sort();

    const columns: ColumnsType<MetricRow> = [
      {
        title: '',
        dataIndex: 'metric',
        key: 'metric',
        fixed: 'left',
      },
      ...dates.map((date) => {
        const formattedDate = formatDateToYYYYMMDD(date);
        return {
          title: (
            <div className="vertical-date-header">
              <span className="vertical-date-text">{formattedDate}</span>
            </div>
          ),
          dataIndex: date,
          key: date,
          align: 'center' as const,
        };
      }),
    ];

    const buildRow = (
      key: string,
      label: string,
      selector: (item: VisaApplicationReportItem) => number
    ) => {
      const row: MetricRow = { key, metric: label };
      dates.forEach((date) => {
        const value = items
          .filter((i) => i.date === date)
          .reduce((sum, i) => sum + (selector(i) ?? 0), 0);
        row[date] = value;
      });
      return row;
    };

    const rows: MetricRow[] = [
      buildRow('success', t('applicationReport.metrics.success'), (i) => i.successCount),
      buildRow('failed', t('applicationReport.metrics.failed'), (i) => i.failedCount),
      buildRow('incomplete', t('applicationReport.metrics.incomplete'), (i) => i.incompleteCount),
      (() => {
        const row: MetricRow = {
          key: 'firstTimeSuccessRate',
          metric: t('applicationReport.metrics.firstTimeSuccessRate'),
        };
        dates.forEach((date) => {
          const relevant = items.filter((i) => i.date === date);
          if (!relevant.length) {
            // No data for this date – show 0 instead of '-'
            row[date] = 0;
            return;
          }
          const sum = relevant.reduce((acc, i) => acc + (i.firstTimeSuccessRate ?? 0), 0);
          const avg = sum / relevant.length;
          row[date] = `${avg.toFixed(2)}%`;
        });
        return row;
      })(),
    ];

    return { columns, rows };
  };

  if (error) {
    const errorWithMessage = error as { message?: string };
    return (
      <ErrorFallback
        details={errorWithMessage.message || 'Failed to load visa application report.'}
        icon={<ExclamationCircleOutlined />}
        error={error}
        resetErrorBoundary={() => {}}
      />
    );
  }

  const hasData = data && data.length > 0;
  const hasWorkflowSelection = selectedWorkflowIds && selectedWorkflowIds.length > 0;

  return (
    <Card className="shadow-lg">
      <Spin spinning={loading}>
        <style>
          {`
          .visa-application-report-row-success td {
            background-color: #f6ffed !important;
          }
          .visa-application-report-row-failed td {
            background-color: #fff1f0 !important;
          }
          .visa-application-report-row-incomplete td {
            background-color: #fffbe6 !important;
          }
          .vertical-date-header {
            height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            min-width: 30px;
          }
          .vertical-date-text {
            transform: rotate(-90deg);
            white-space: nowrap;
            font-size: 12px;
            font-weight: 500;
            display: inline-block;
            transform-origin: center;
            line-height: 1.2;
          }
          .ant-table-thead > tr > th {
            vertical-align: bottom;
            text-align: center;
          }
          .ant-table-thead > tr > th:not(:first-child) {
            min-width: 40px;
          }
        `}
        </style>

        <div className="mb-4">
          <div className="row g-3">
            <div className="col-md-4 col-sm-12">
              <Text strong>{t('applicationReport.workflowLabel')}</Text>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('applicationReport.workflowPlaceholder')}
                options={workflowOptions}
                value={selectedWorkflowIds}
                onChange={onWorkflowChange}
                maxTagCount="responsive"
              />
            </div>
            <div className="col-md-4 col-sm-12">
              <Text strong>{t('applicationReport.showByLabel')}</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={dateRange}
                onChange={onDateRangeChange}
                options={[
                  {
                    label: DATE_RANGE_LABELS[DashboardDateRangeEnum.DAY],
                    value: DashboardDateRangeEnum.DAY,
                  },
                  {
                    label: DATE_RANGE_LABELS[DashboardDateRangeEnum.MONTH],
                    value: DashboardDateRangeEnum.MONTH,
                  },
                  {
                    label: DATE_RANGE_LABELS[DashboardDateRangeEnum.QUARTER],
                    value: DashboardDateRangeEnum.QUARTER,
                  },
                ]}
              />
            </div>
          </div>
        </div>

        {!hasWorkflowSelection && !loading && (
          <Empty description={t('applicationReport.empty.selectWorkflow')} />
        )}

        {hasWorkflowSelection && !hasData && !loading && (
          <Empty description={t('applicationReport.empty.noData')} />
        )}

        {hasData &&
          Object.entries(groupedByWorkflow).map(([workflowKey, items]) => {
            const { columns, rows } = buildTableForWorkflow(items);
            const workflowName = items[0]?.workflowNames?.[0] || workflowKey;
            const columnsWithWorkflowHeader = [
              { ...columns[0], title: workflowName },
              ...columns.slice(1),
            ];

            return (
              <div key={workflowKey} className="mb-5">
                <div className="table-responsive table-responsive-sm table-responsive-md table-responsive-lg table-responsive-xl">
                  <Table<MetricRow>
                    columns={columnsWithWorkflowHeader}
                    dataSource={rows}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: 'max-content' }}
                    rowClassName={(record) => {
                      if (record.key === 'success') return 'visa-application-report-row-success';
                      if (record.key === 'failed') return 'visa-application-report-row-failed';
                      return '';
                    }}
                    bordered
                    size="small"
                  />
                </div>
              </div>
            );
          })}
      </Spin>
    </Card>
  );
};

export default React.memo(VisaApplicationReportTable);
